import { Link } from "react-router-dom";
import { ArrowRight, Heart, Zap, Globe, Award, Users, Target } from "lucide-react";
import CTASection from "../components/CTASection";
import Testimonials from "../components/Testimonials";

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="hero-gradient relative overflow-hidden grain">
        <div className="absolute -top-20 left-1/3 w-80 h-80 rounded-full bg-[#FFE4E8] opacity-50 blur-3xl animate-blob" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32 text-center">
          <span className="inline-block px-4 py-1.5 rounded-full glass-strong border border-[#1B4332]/10 text-black text-xs font-semibold mb-6">About Brivo</span>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold text-black tracking-tight leading-[1.05]">
            We build software for<br />
            <span className="italic text-black/60 font-medium">the people who build everything else.</span>
          </h1>
          <p className="mt-7 text-lg md:text-xl text-black/70 max-w-2xl mx-auto leading-relaxed">
            Brivo is an all-in-one platform helping ambitious business owners run their operations
            without burning out. Founded in 2022, used by 12,000+ businesses across 30 countries.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#FFF9E6] text-black text-xs font-semibold mb-4">Our mission</span>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold text-black tracking-tight">
              Make running a business <span className="italic text-black/60 font-medium">feel less like a fight.</span>
            </h2>
          </div>
          <p className="text-lg text-black/75 leading-relaxed text-center max-w-3xl mx-auto">
            We've watched too many talented people pour their hearts into a business — only to be drowned by spreadsheets, broken integrations, and software that wasn't built for them.
            Brivo exists to give every business owner — from a solo Etsy seller to a 200-store chain — the same operational power as the biggest companies in the world. Without the cost. Without the complexity.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32 bg-[#FFFCF2]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#FFE4E8] text-black text-xs font-semibold mb-4">Our values</span>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold text-black tracking-tight">What we stand for</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: <Heart size={22} />, t: "Customer obsession", d: "Every roadmap decision starts with: 'will this make our customers' lives easier?'" },
              { icon: <Zap size={22} />, t: "Speed over perfection", d: "We ship fast, listen, then ship faster. Perfect is the enemy of useful." },
              { icon: <Globe size={22} />, t: "Built for the world", d: "From Lagos to London, we design for the global majority — not just Silicon Valley." },
              { icon: <Award size={22} />, t: "Quality you can feel", d: "Beautiful interfaces, sub-100ms responses, zero-bug releases. The little things matter." },
              { icon: <Users size={22} />, t: "Honest partnership", d: "No surprise fees, no lock-in tricks. We win when our customers win." },
              { icon: <Target size={22} />, t: "Outcomes, not features", d: "We measure success by your revenue growth, not the size of our changelog." },
            ].map((v) => (
              <div key={v.t} className="bg-white rounded-3xl p-7 border border-[#1B4332]/8 card-hover">
                <div className="w-12 h-12 rounded-xl bg-[#FFF9E6] flex items-center justify-center text-black mb-5">{v.icon}</div>
                <h3 className="font-display font-bold text-xl text-black mb-2">{v.t}</h3>
                <p className="text-black/70 leading-relaxed text-sm">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 md:py-28 bg-[#1B4332] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-4 gap-10 text-center">
            {[
              { v: "12,000+", l: "Businesses" },
              { v: "30+", l: "Countries" },
              { v: "$2.4B", l: "Processed" },
              { v: "99.99%", l: "Uptime" },
            ].map((s) => (
              <div key={s.l}>
                <div className="font-display font-extrabold text-5xl md:text-6xl tracking-tight">{s.v}</div>
                <div className="text-white/70 mt-2 uppercase tracking-widest text-sm font-semibold">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 md:py-32 bg-[#E8F5E9]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-white text-black text-xs font-semibold mb-4">Our people</span>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold text-black tracking-tight">
              The team behind Brivo
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { n: "Adunni Ola", r: "Co-founder & CEO", img: "https://images.unsplash.com/photo-1758887261865-a2b89c0f7ac5?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" },
              { n: "Marcus Chen", r: "Co-founder & CTO", img: "https://images.unsplash.com/photo-1765648636178-60e73bcc865e?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" },
              { n: "Priya Mensah", r: "Head of Product", img: "https://images.unsplash.com/photo-1769636930047-4478f12cf430?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" },
            ].map((p) => (
              <div key={p.n} className="bg-white rounded-3xl overflow-hidden border border-[#1B4332]/5 card-hover">
                <img src={p.img} alt={p.n} className="w-full aspect-[4/5] object-cover" />
                <div className="p-6">
                  <div className="font-display font-bold text-xl text-black">{p.n}</div>
                  <div className="text-sm text-black/60 mt-1">{p.r}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Testimonials />
      <CTASection accent="cream" title="Want to join us?" subtitle="We're always hiring great people. See open roles." />
    </div>
  );
}
